import { useLabs } from "../grid/etLabs";
import React from "react";
import { Box, Tooltip } from "@mui/material";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useEtBreakpoints, useEtViewMode, useEtMode, cycleEtMode } from "./useEtView";

/**
 * Small floating ET control, docked on the BOTTOM. Two compact pills:
 *   • Phone / Full — switch layout (works on shared /s/ links too).
 *   • ‹ Mode › — in the phone layout, cycle through preset looks (Simple / Compact / Cards /
 *     Media) with the arrows, so you can flip between them and see which you like.
 * Choices are remembered per browser (see useEtView).
 */
const font = "Outfit, sans-serif";

const pill = {
  display: "inline-flex",
  alignItems: "center",
  gap: "2px",
  padding: "3px",
  borderRadius: "999px",
  backgroundColor: "rgba(18,17,24,0.94)",
  border: "1px solid rgba(255,255,255,0.14)",
  boxShadow: "0 6px 22px rgba(0,0,0,0.5)",
  backdropFilter: "blur(6px)",
};

const seg = (active) => ({
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
  border: "none",
  fontFamily: font,
  fontWeight: 700,
  fontSize: "12.5px",
  lineHeight: 1,
  padding: "7px 12px",
  borderRadius: "999px",
  color: active ? "#fff" : "#c2c7d6",
  background: active ? "#e61d50" : "transparent",
  transition: "background 0.15s, color 0.15s",
});

const arrow = {
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  background: "transparent",
  color: "#c2c7d6",
  borderRadius: "999px",
  padding: "5px",
  "&:hover": { background: "rgba(255,255,255,0.1)", color: "#fff" },
};

// True when the editor is running inside an iframe — i.e. embedded in a landing-page demo
// (Letters K/O). Those demos drive the layout with their OWN Desktop/Phone toggle (by sizing
// the iframe), so our floating toggle would just be redundant clutter poking through the demo.
const isEmbedded = () => { try { return window.self !== window.top; } catch (e) { return true; } };

export const EtViewToggle = () => {
  const { isXSmallScreen } = useEtBreakpoints();
  const [, setMode] = useEtViewMode();
  const [, mode] = useEtMode();
  const phoneActive = isXSmallScreen;

  const labs = useLabs();
  // Hide inside the landing-demo iframes; the demo's own Desktop/Phone toggle controls the view.
  if (isEmbedded()) return null;
  const onLeft = !!labs.toggleLeft;

  return (
    <Box
      sx={{
        position: "fixed",
        // On a phone the bottom-right corner is where the tool bubbles and Play now live, and
        // this sat on top of them at a higher z-index — it painted over Settings and Reset and
        // swallowed the taps. Move it to the top-right there; desktop keeps the bottom corner.
        top: { xs: 8, sm: "auto" },
        bottom: { xs: "auto", sm: 16 },
        // Bottom-RIGHT is where every row's timecode steppers and download arrow live in the
        // Full view, so the toggle sat permanently on top of the last visible row's controls
        // (Isaac, 2026-09-24). The left gutter is empty there. Phone keeps the right corner,
        // where it is already pinned to the top.
        ...(onLeft ? { left: { xs: "auto", sm: 12 }, right: { xs: 12, sm: "auto" } } : { right: 12 }),
        zIndex: 10900,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: "8px",
        opacity: 1,
      }}
    >
      {/* mode cycler — sits on top; only in the compact layout */}
      {phoneActive && (
        <Box sx={pill}>
          <Tooltip title="Previous look" placement="top" arrow>
            <Box component="button" onClick={() => cycleEtMode(-1)} sx={arrow} aria-label="Previous look">
              <KeyboardArrowLeftIcon sx={{ fontSize: 18 }} />
            </Box>
          </Tooltip>
          <Tooltip title={mode.hint} placement="top" arrow>
            <Box sx={{ fontFamily: font, fontWeight: 800, fontSize: "12.5px", color: "#fff", px: "6px", minWidth: 62, textAlign: "center", whiteSpace: "nowrap" }}>
              {mode.name}
            </Box>
          </Tooltip>
          <Tooltip title="Next look" placement="top" arrow>
            <Box component="button" onClick={() => cycleEtMode(1)} sx={arrow} aria-label="Next look">
              <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
            </Box>
          </Tooltip>
        </Box>
      )}

      {/* layout: Phone / Full — below the mode cycler */}
      <Box sx={pill}>
        <Tooltip title="Compact mobile layout" placement="top" arrow>
          <Box component="button" onClick={() => setMode("phone")} sx={seg(phoneActive)} aria-pressed={phoneActive}>
            <PhoneIphoneIcon sx={{ fontSize: 16 }} /> Phone
          </Box>
        </Tooltip>
        <Tooltip title="Full spreadsheet — every column" placement="top" arrow>
          <Box component="button" onClick={() => setMode("full")} sx={seg(!phoneActive)} aria-pressed={!phoneActive}>
            <DesktopWindowsIcon sx={{ fontSize: 16 }} /> Full
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default EtViewToggle;
